import {
    ApiResponseError,
    ToastType,
    showErrorToast,
    showSuccessToast,
    toastState,
    useApiRequest
} from 'contexts/apiRequestContext';

import { useDatasetData, useDatasetMutation } from 'contexts/datasetContext';
import { useLocation, useParams } from 'react-router';
import DatasetWrapper from '../DatasetWrapper';
import { HandleTitle } from '../DatasetNaturalPerson/TypographyAndValues';
import { Alert } from '@uy3/web-components';
import ScrList from './ScrList';
import React, { useMemo, useState } from 'react';
import Toast from 'components/Toast/Toast';
import { GroupedButtons } from '@uy3/web-components';
import { Box, Typography } from '@mui/material';
import { ScrDetailsContainer } from './SrcDetails';

function formatYearMonthToMonthYear(date: string | null) {
    return date != null ? date.substring(5) + '/' + date.substring(0, 4) : 'N/D';
}

type SCRDataProps = {
    id?: string | undefined;
    discriminator?: string | undefined;
    disableRequestDataSet: boolean;
};
export const contentStyle: React.CSSProperties = {
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    maxWidth: '100%'
};
export const SCR_VIEW_OPTIONS = [
    { label: 'Consolidado', value: 'CONSOLIDATED' },
    { label: 'Detalhado', value: 'DETAILED' }
];

export function SCRData({ id, discriminator, disableRequestDataSet }: SCRDataProps) {
    const [toast, setToast] = useState<ToastType>(toastState);
    const { pathname } = useLocation();
    const isNaturalPerson = pathname.includes('pessoas-fisicas');
    const [selectedView, setSelectedView] = useState('CONSOLIDATED');
    const recordType =
        isNaturalPerson || discriminator === 'NaturalPerson' ? 'NaturalPerson' : 'LegalPerson';
    const { personId } = useParams();
    const dataId = id ?? personId;

    const {
        dataset,
        status,
        refetch,
        isLoading: isLoadingDataSetData
    } = useDatasetData(recordType, dataId!, 'ScrBcb');
    const { setSubmitError } = useApiRequest();

    const onSuccess = () => refetch();
    const onError = (error: ApiResponseError) => setSubmitError(error);

    const {
        mutate,
        isLoading: isLoadingDataSetMutation,
        status: statusDataSet
    } = useDatasetMutation(recordType, dataId!, onSuccess, onError);
    const handleUpdateData = async (values: string) => {
        mutate(values);
    };

    const data = dataset?.find((dataset) => dataset?.dataSet === 'scr_bcb') as any;
    const result = data?.result;

    let fluxoConsolidado = result?.fluxoConsolidado;
    let msgs = result?.listaDeMensagensDeValidacao;
    let dataSCR = data?.result?.dataBaseConsultada;
    let error = result?.Status?.scr_bcb && result?.Status.scr_bcb[0]?.Message;

    useMemo(() => {
        if ((status === 'success' || statusDataSet === 'success') && result?.length > 0) {
            const title = 'SRC consultado com sucesso!';
            const description = 'Ótimo! agora você pode visualizar o registro consultado.';
            showSuccessToast(title, description, setToast);
        } else if (status === 'error' || statusDataSet === 'error') {
            const title = 'Ops, ocorreu um erro ao consultar o SCR!';
            const description = error;
            showErrorToast(title, description, setToast);
        }
    }, [status, error, statusDataSet, result]);

    const getOptions = () => SCR_VIEW_OPTIONS.map((opt) => opt.label);

    const onGetOptionFilterSelected = (index: number) => {
        setSelectedView(SCR_VIEW_OPTIONS[index].value);
    };

    const renderConsolidated = (
        <Box
            sx={{
                '& .MuiGrid-item': {
                    display: 'flex',
                    justifyContent: 'flex-end',
                    margin: '-90px 40px 30px 0'
                }
            }}
        >
            {!result && (
                <Box
                    sx={{
                        backgroundColor: '#F4F3F3',
                        height: '104px',
                        p: 4,
                        borderRadius: 2
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: 'Lato, sans-serif',
                            fontWeight: 400,
                            fontSize: '19px',
                            color: '#292927'
                        }}
                    >
                        O SCR ainda não foi consultado. Clique em 'Obter dados' para iniciar a
                        verificação.
                    </Typography>
                </Box>
            )}
            {fluxoConsolidado && <ScrList fluxoConsolidado={fluxoConsolidado} />}
            <DatasetWrapper
                dataSet={data}
                disableRequestDataSet={disableRequestDataSet}
                handleUpdateData={() => handleUpdateData('ScrBcb')}
                isLoading={isLoadingDataSetMutation || status === 'loading'}
            />
        </Box>
    );

    const renderDetailed = (
        <ScrDetailsContainer
            data={data}
            handleUpdateData={handleUpdateData}
            disableRequestDataSet={isLoadingDataSetData || isLoadingDataSetMutation}
        />
    );

    return (
        <>
            <Toast toast={toast} setToast={setToast} />
            {result && (
                <Box
                    sx={{
                        marginBottom: 2,
                        '& > div': {
                            fontWeight: 600,
                            fontFamily: 'Lato, sans-serif',
                            backgroundColor: '#FAFAF9',
                            fontSize: '14px',
                            border: '0.5px solid #52524D',
                            borderRadius: 2
                        },
                        '& > div > button': {
                            borderRadius: '12px'
                        }
                    }}
                >
                    <GroupedButtons
                        groupedButtons={getOptions()}
                        onClick={onGetOptionFilterSelected}
                        size="large"
                    />
                </Box>
            )}

            {msgs &&
                msgs.map((m: any) => (
                    <Alert severity="info" text={`${m.codigo} - ${m.mensagem}`} />
                ))}
            {error && <Alert severity="error" text={error} />}

            {dataSCR && (
                <HandleTitle
                    title="Data-Base"
                    value={formatYearMonthToMonthYear(dataSCR)}
                    key="dataBaseConsultada"
                />
            )}

            {selectedView === 'CONSOLIDATED' ? renderConsolidated : renderDetailed}
        </>
    );
}
